import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { RefreshCw } from "lucide-react";
import type { UseMutationResult } from "@tanstack/react-query";
import { Button } from "@/shared/components/ui/button";
import { resolveMutationErrorMessage } from "@/shared/api/error-message";

type Status = "idle" | "success" | "error";

const FEEDBACK_MS = 4_000;

interface CacheRefreshButtonProps {
  // Mutation hook result. The button drives a `void → void` call, so any
  // mutation that fits that shape can plug in (catalog cache, feature
  // flags cache, anything new behind an admin refresh endpoint).
  readonly mutation: UseMutationResult<unknown, unknown, void>;
  readonly labels: {
    readonly idle: string;
    readonly pending: string;
    readonly success: string;
  };
  // Translation keys forwarded to `resolveMutationErrorMessage` so 401/403
  // and server-message branches use the caller's domain copy.
  readonly errorKeys: {
    readonly forbiddenKey: string;
    readonly serverPrefixKey: string;
    readonly genericKey: string;
  };
  readonly testIdPrefix: string;
}

export function CacheRefreshButton({
  mutation,
  labels,
  errorKeys,
  testIdPrefix,
}: CacheRefreshButtonProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear any pending success-flash timer on unmount so we don't `setState`
  // after the component is gone (React 18 / Strict Mode warning, real leak
  // on slow nav). Same cleanup runs before every new click via the manual
  // call below.
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, []);

  const handleClick = () => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setStatus("idle");
    setErrorMessage(null);
    mutation.mutate(undefined, {
      onSuccess: () => {
        setStatus("success");
        timerRef.current = setTimeout(() => {
          setStatus("idle");
          timerRef.current = null;
        }, FEEDBACK_MS);
      },
      onError: (err) => {
        setErrorMessage(resolveMutationErrorMessage(err, t, errorKeys));
        setStatus("error");
      },
    });
  };

  return (
    <div className="flex flex-col items-end gap-1">
      <Button
        data-testid={`${testIdPrefix}-button`}
        variant="outline"
        onClick={handleClick}
        disabled={mutation.isPending}
      >
        <RefreshCw
          className={`mr-2 h-4 w-4 ${mutation.isPending ? "animate-spin" : ""}`}
          aria-hidden="true"
        />
        {mutation.isPending ? labels.pending : labels.idle}
      </Button>
      {status === "success" && (
        <p
          data-testid={`${testIdPrefix}-success`}
          role="status"
          className="font-sans text-xs font-medium text-emerald-600"
        >
          {labels.success}
        </p>
      )}
      {status === "error" && errorMessage && (
        <p
          data-testid={`${testIdPrefix}-error`}
          role="alert"
          className="font-sans text-xs font-medium text-destructive"
        >
          {errorMessage}
        </p>
      )}
    </div>
  );
}
